import React, { FunctionComponent, useCallback } from "react";
import { Typography } from "@mui/material";
import { TreeItem } from "@mui/lab";

import "../../../css/shared/tree_item.css";
import MenuItemSelectionIcon from "../filter_bar/filter_bar_menu_item/menu_item_selection_icon";
import useToggle from "../../services/component_helpers/component_effects/use_toggle";
import { type Filter, isFilter } from "../../services/utilities/filter";

import type { BreakdownNode } from "../../services/utilities/breakdown";
import type { Selected } from "type_aliases";
import classNames from "classnames";

type DefaultProps = {
  node: BreakdownNode<{ title?: string, subtitle?: string, label?: string, id: string, disabled?: boolean } & Record<string, any>>
}

export type SelectableProps = DefaultProps & {
  selectedElements?: Filter | { [key: string]: Selected }
  onSelect?: (payload: string | number) => void
  onDeselect?: (payload: string | number) => void
}

export type Props = DefaultProps | SelectableProps

const areSelectableProps = (props: any): props is SelectableProps => {
  return !!props.onSelect;
};
const AvvirTreeItem: FunctionComponent<Props> = function AvvirTreeItem(props) {
  const { node } = props;
  let selectedElements: SelectableProps["selectedElements"], onSelect: SelectableProps["onSelect"], onDeselect: SelectableProps["onDeselect"];
  if (areSelectableProps(props)) {
    selectedElements = props.selectedElements;
    onSelect = props.onSelect;
    onDeselect = props.onDeselect;
  }

  let checked: boolean, indeterminate: boolean;
  if (isFilter(selectedElements)) {
    checked = !!selectedElements?.[node.id]?.selected;
    indeterminate = selectedElements?.[node.id]?.selected === -1;
  } else {
    checked = !!selectedElements?.[node.id];
    indeterminate = selectedElements?.[node.id] === -1;
  }
  const selectable = !!selectedElements;
  const hasChildren = Array.isArray(node.children) && node.children.length > 0;

  const handleSelect = useCallback(() => {
    onSelect(node.id);
  }, [onSelect, node.id]);

  const handleDeselect = useCallback(() => {
    onDeselect(node.id);
  }, [onDeselect, node.id]);
  const handleClick = useToggle(handleSelect, handleDeselect, checked);

  return <TreeItem
    disabled={node.disabled}
    className={classNames("AvvirTreeItem", {"AvvirTreeItem--disabled": node.disabled})}
    label={
      <div className="AvvirTreeItem-content">
            <span className="AvvirTreeItem-contentLabel">
              <Typography>{node.label || node.title || node.id}</Typography>
              {node.subtitle ? <Typography variant="label" lineHeight={1}>{node.subtitle}</Typography> : null}
            </span>
        {selectable ?
         <div className="AvvirTreeItem-selectionIcon">
           <MenuItemSelectionIcon
             onClick={handleClick}
             checked={checked}
             indeterminate={indeterminate}
             dataValue={node.id}
             multiSelect
           />
         </div> : null}
      </div>
    }
    nodeId={node.id}
    classes={{
      group: "AvvirTreeItem-group",
      content: "AvvirTreeItem-content",
      label: "AvvirTreeItem-label",
      root: "AvvirTreeItem-root"
    }}
    {...(selectable && !hasChildren && !node.disabled && { onClick: handleClick })}
  >
    {hasChildren ? node.children.map((childNode: BreakdownNode) => {
      return <AvvirTreeItem selectedElements={selectedElements} node={childNode} key={`AvvirTreeItem-${childNode.id}`} onSelect={onSelect} onDeselect={onDeselect}/>;
    }) : null}
  </TreeItem>;
};

export default React.memo<Props>(AvvirTreeItem);
